import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, HardDrive, Sparkles, Eye, Printer } from '../../icons';
import {
  createDocument, extractErrorCode, extractErrorMessage, listDocuments,
  INACTIVE_DOC_TYPE_CODE, UNKNOWN_DOC_TYPE_CODE,
} from '../../api/approval';
import { listDocTypes, type DocTypeDto } from '../../api/approval-doc-type';
import type { LineSuggestionDto } from '../../api/hr-org-leader';
import { ApprovalLineBlock, EMPTY_LINE, type ApprovalLineValue } from './ApprovalLineBlock';
import { ApprovalFormFields, type FormValues } from './ApprovalFormFields';
import { BudgetHint } from './BudgetHint';
import { selectCode } from '../../lib/approval-select';
import { Button, Checkbox, DocTitleInput, FormField, Input, Textarea, UnsavedGuard } from '../../components/shared';
import { ApprovalPreview } from './ApprovalPreview';
import { useAttachmentUpload, AttachmentUploader, WorkspaceFilePicker } from '../../components/Attachment';
import { MAX_ATTACHMENTS } from '../../lib/attachments';
import { useAuthStore } from '../../store/authStore';
import { useApprovalStore } from '../../store/approvalStore';
import './ApprovalNew.css';

/**
 * 기안 — **창이 아니라 자리다** (2026-08-31 · 시안 「기안하는 자리」 A·B·C).
 *
 * ## ★★★ 왜 모달을 벗었나
 *
 * 종전 기안 창은 `min(560px, 90vw)` 였다. 서식은 칸을 **최대 마흔**까지 갖고 그중 표 칸이
 * 있으면 그 창에서 **가로로 짜부러진다**(코퍼스 실측: 칸 12개 넘는 양식 136 · 표를 쓰는 양식 137).
 * 그래서 문서는 넓게 두고, **종류와 무관한 것**(결재선 · 첨부 · 옵션)을 오른쪽 기둥으로 뺐다 —
 * *「위 칸(서식)만 종류마다 다르고 아래는 똑같다」* 를 배치로 굳힌 것이다.
 *
 * ## ★★ 양식을 먼저 고른다 (시안 B)
 *
 * 종전에는 유형이 **세 번째 칸**이었다. 유형이 서식을 정하는데 본문 다음이라, 본문까지 쓰고
 * 유형을 바꾸면 그 아래가 통째로 갈렸다. 먼저 고르게 하면 그 사고가 **구조적으로** 사라진다.
 * ★ **회사가 지은 이름을 그대로 쓴다** — 종전에는 아는 코드에 번역 라벨을 씌워, 「양식 · 서식」에
 *   「휴가 신청」으로 등록한 것이 기안 화면에서는 「연차/휴가」로 떴다.
 *
 * ## ★★ 문서처럼 보이게 (시안 A)
 *
 * **결재란**(도장 칸)과 **기안자 · 소속 · 기안일**을 문서 머리에 세운다. 인쇄하면 함께 나가는
 * 그 칸을, 쓰는 순간에는 못 보던 것이 종전이다. 값은 **이미 다 있다** — 사람은 로그인에서,
 * 소속과 결재 사다리는 결재선 추천(`/approval/line-suggestion`)에서 온다. **새 API 가 없다.**
 *
 * ## ★ 안 그리는 것 둘
 *
 * - **문서번호** — 2026-09-04(판 P4)부터 **상신하는 순간 서버가 매긴다**(`회사코드-연도-일련`).
 *   ★ 그래도 **이 화면에는 안 그린다** — 올리기 전에는 번호가 없다(있다고 그리면 거짓말이고,
 *     자리만 비워 두면 「왜 비었나」가 남는다). 번호는 **문서 상세**에서 보인다.
 * - **임시저장 · 미리 보기** — 담을 표도 화면도 없다. 시안은 그렸지만 **할 수 없는 일을 세우지 않는다**.
 */

/** 옛 서버(레지스트리 404)일 때의 되돌아갈 목록 — 종전 `ApprovalCompose` 와 같은 값이다. */
const FALLBACK_TYPES = [
  { code: 'LEAVE', name: '휴가 신청' },
  { code: 'BUSINESS_TRIP', name: '출장 신청' },
  { code: 'OVERTIME', name: '연장근로 신청' },
  { code: 'EARLY_LEAVE', name: '조퇴 신청' },
  { code: 'EXPENSE', name: '지출결의서' },
  { code: 'OTHER', name: '기타' },
] as const;

const GROUPS: Array<{ key: string; label: string }> = [
  { key: 'HR', label: '근태 · 휴가' },
  { key: 'GENERAL', label: '그 외' },
];

/** 오늘 — 문서 머리의 기안일. 화면이 그리는 값이라 서버 시각을 따로 묻지 않는다. */
function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * 「결재양식」 창이 들고 오는 값 (2026-09-07 · 시안 A1).
 *
 * ★ 주소가 아니라 **라우터 state** 로 온다 — 제목에 «/» 나 «?» 가 들어가는 일이 흔하고,
 *   주소에 실으면 그때 깨진다.
 * ★ 직접 `/approval/new` 로 들어오면 아무것도 안 온다 → 종전대로 이 화면의 첫 걸음이 고른다.
 */
interface PickedFromModal {
  type?: string;
  title?: string;
  deptBoxId?: string | null;
  /**
   * ── 아래 셋은 **AI 회의록 보내기**가 넘겨준다 (2026-09-22 · additive).
   *
   * 「결재로 올리기」를 고르면 회의록 화면이 이 화면으로 **넘어오면서** 제목·본문을 채우고
   * `minutesId` 를 들려 보낸다. 결재선은 **여기서** 고른다(상신에 결재자가 1명 이상 필요하고,
   * 회의록 화면이 그것을 흉내 내면 규칙이 두 곳이 된다).
   *
   * ★ 안 오면 전부 `undefined` 이고 **종전과 똑같이** 동작한다.
   */
  content?: string;
  /**
   * ⛔ 회의록 글은 **줄바꿈이 뜻을 갖는다** — 기본값 `HTML` 로 보내면 요약·결정·액션이 한 덩이로
   *   붙는다(서버 메일 갈래가 같은 까닭으로 `TEXT` 를 쓴다). 그래서 넘어올 때 `TEXT` 를 함께 준다.
   */
  contentType?: 'HTML' | 'TEXT';
  /** 어느 회의록에서 왔나 — 서버가 이 값으로 회의록 「보낸 이력」에 한 줄을 적는다. */
  minutesId?: string;
}

export function ApprovalNewPage(): JSX.Element {
  const navigate = useNavigate();
  const handoff = (useLocation().state ?? null) as PickedFromModal | null;
  const me = useAuthStore((s) => s.user);
  const myUserId = me ? String(me.id) : null;

  const [searchParams] = useSearchParams();
  const [docTypes, setDocTypes] = useState<DocTypeDto[] | null>(null);
  /** 최근에 쓴 종류 — 사람은 대개 **같은 것을 다시** 쓴다. 못 읽으면 빈 배열(길만 안 선다). */
  const [recent, setRecent] = useState<string[]>([]);
  /**
   * 고른 양식. `null` 이면 아직 첫 걸음(시안 B)이다.
   * ★ 「결재양식」 창에서 왔으면 이미 골라져 있다 — 그 첫 걸음을 건너뛴다.
   */
  const [type, setType] = useState<string | null>(handoff?.type ?? null);

  /** ★ 제목도 창에서 미리 차 온다(양식 이름 또는 회사가 정한 기본 제목). */
  const [title, setTitle] = useState(handoff?.title ?? '');
  /** 부서 결재함 — 창에서 고른 것을 그대로 들고 간다. 대개 「미지정」(`null`)이다. */
  const [deptBoxId] = useState<string | null>(handoff?.deptBoxId ?? null);
  // ★ 기본값은 종전과 같은 빈 글자다 — 회의록에서 넘어올 때만 채워진다(additive).
  const [content, setContent] = useState(handoff?.content ?? '');
  /**
   * 본문을 무엇으로 보낼까 — 종전 그대로 **`HTML` 이 기본**이다.
   * ⛔ 회의록에서 넘어온 글만 `TEXT` 다(줄바꿈이 뜻을 갖는다). 안 넘어오면 값이 같아 동작이 같다.
   */
  const [contentType] = useState<'HTML' | 'TEXT'>(handoff?.contentType ?? 'HTML');
  /** 어느 회의록에서 왔나 — 제출 때 «있을 때만» 싣는다(옛 서버는 모르는 칸이라 400 이다). */
  const [minutesId] = useState<string | null>(handoff?.minutesId ?? null);
  const [line, setLine] = useState<ApprovalLineValue>(EMPTY_LINE);
  const [suggestion, setSuggestion] = useState<LineSuggestionDto | null>(null);
  const [formValues, setFormValues] = useState<FormValues>({});
  const [measure, setMeasure] = useState('');
  const [isUrgent, setIsUrgent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  /**
   * 올렸는가 (2026-09-08) — 이탈 경고를 **끄는** 신호다.
   *
   * ★ 올린 뒤에도 이 화면에 남는 갈래가 있다(근태에 못 만든 사유 · 예산 알림). 그때는
   *   **문서가 이미 생겼으므로** 잃을 것이 없다 — `navigate` 자리마다 끄지 않고 여기 한 곳에서 끈다.
   */
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 잃을 것이 있는가 — 이탈 경고의 재료 (2026-09-08 사용자 지시 · 정본 P13).
   *
   * ★★★ **«사람이 넣은 글자»만 센다.** 결재선(`line`)은 서버가 기본값을 «자동 배치»하고
   *    서식 칸은 종류를 고를 때 비워진다 — 그런 자동 채움을 「고쳤다」로 세면 **아무것도
   *    안 한 사람에게까지 창이 뜨고, 그러면 사람은 창을 안 읽고 누르게 된다.**
   * ★ 제목은 넘겨받은 값(`handoff`)과 견준다 — 양식에서 들어오면 제목이 미리 차 있다.
   */
  const dirty = !submitted && !submitting && (
    title.trim() !== (handoff?.title ?? '').trim()
    || content.trim() !== ''
    || measure.trim() !== ''
    || Object.values(formValues).some((v) => v !== '' && v != null && v !== false)
  );
  /**
   * 「올라갔는데 근태에는 안 들어갔다」 (2026-09-01 · 하네스 판 C).
   *
   * ★ 이때는 **넘어가지 않는다.** 목록으로 보내 버리면 사람은 반영된 줄 알고, 연차는 그대로
   *   남는다 — 그 상태를 아무도 못 봤던 것이 이 판의 출발점이다.
   */
  /** 지금 올리는 중인가 — **상태가 아니라 ref** 다(그림과 무관하게 즉시 바뀐다). */
  const sendingRef = useRef(false);
  const [hrNotice, setHrNotice] = useState<string | null>(null);
  /**
   * 회의록 「보낸 이력」에 못 적혔다 (2026-09-22 · additive).
   *
   * ★★ `hrNotice` 와 **뜻이 다르다** — 저것은 「근태에 반영이 안 됐다」이고 이것은
   *   **「결재는 올라갔지만 회의록에서 되짚을 줄이 없다」**다. 되돌리기가 없는 기능에서 그 이력이
   *   유일한 되짚을 길이라, 조용히 넘어가면 사람은 **안 남은 것을 모른다.**
   * ★ 그래서 위 근태·지출과 **같은 규칙**으로 «여기 남아서 말한다»(문서함으로 안 넘긴다).
   */
  const [minutesNotice, setMinutesNotice] = useState<string | null>(null);
  /**
   * 기안 종류를 **못 쓴다** (2026-09-22 · #1626) — 없는 종류이거나 회사가 껐다.
   *
   * ★ `error`(붉은 한 줄)와 따로 둔다: 「관리자에게 문의하세요」는 **행동을 요구하는 말**이라
   *   한 줄 오류에 섞이면 읽히지 않는다. 그리고 **다시 눌러도 안 되는** 상태라 그 사실을 말해야 한다.
   * ★ `code` 를 함께 들고 있는 까닭 — 문구는 서버가 주지만 **「무엇을 할 수 있는지」는 화면이 적는다.**
   */
  const [docTypeNotice, setDocTypeNotice] = useState<{ code: string; message: string } | null>(null);
  /**
   * **올라갔는데 예산에는 안 붙었다** (2026-09-04 · 예산 판 B3).
   *
   * ★★ `hrNotice` 와 **뜻이 다르다.** 저것은 「반영이 안 됐다」(잘못)이고, 이것은
   *    「예산 없이 올렸다」(잘못이 아님)다 — 예산을 안 짠 회사도 지출결의서를 쓴다.
   *    그래서 **문서함으로 보내지 않고**, 알리기만 하고 그대로 넘어간다.
   */
  const [budgetNotes, setBudgetNotes] = useState<string[]>([]);
  const attachState = useAttachmentUpload({ onError: setError });
  const [wsPickerOpen, setWsPickerOpen] = useState(false);
  /**
   * ★★★ **미리보기 — 올리기 전에 완성된 문서를 본다** (2026-09-01 · 판 13 ③).
   *    종전에는 「올리기」를 누르는 순간이 곧 처음 보는 순간이었다. 회수하면 상대의
   *    결재함에 이미 흔적이 남는다. **저장하지 않는다** — 화면 안에서 모습만 바꾼다.
   */
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    listDocTypes().then((t) => setDocTypes(t.length > 0 ? t : null)).catch(() => setDocTypes(null));
    // 최근에 쓴 종류 — 내가 올린 것에서 앞쪽 몇 개의 종류를 순서대로 모은다.
    listDocuments('drafted', { limit: 20 })
      .then((r) => setRecent([...new Set(r.items.map((d) => d.type))].slice(0, 3)))
      .catch(() => setRecent([]));
  }, []);

  // ★ 종류를 바꾸면 서식 값을 비운다 — 앞 종류의 값이 다음 종류로 흘러들면 안 된다.
  useEffect(() => { setFormValues({}); }, [type]);

  /** 고를 수 있는 양식 — **회사가 지은 이름 그대로**. 옛 서버면 코드 배열로 되돌아간다. */
  const types = useMemo(
    () => (docTypes ?? FALLBACK_TYPES.map((t) => ({ ...t, category: 'GENERAL' }) as unknown as DocTypeDto)),
    [docTypes],
  );

  /**
   * 양식을 미리 고르고 연다 — `?type=LEAVE` (2026-09-03 · 개인 근태 P3-3).
   *
   * ★★ **목록이 온 뒤에, 그 목록에 있는 코드일 때만** 고른다. 없는 코드를 그대로 넣으면
   *    첫 걸음(시안 B)이 «고른 것처럼» 보이는데 아래는 텅 비어, 사용자가 무엇을 잘못했는지
   *    알 수 없게 된다. 없으면 **평소처럼 고르는 화면**으로 둔다.
   * ★ 한 번만 고른다 — 사용자가 뒤에 종류를 바꾸면 그 선택이 이긴다.
   */
  const preset = searchParams.get('type');
  const [presetDone, setPresetDone] = useState(false);
  useEffect(() => {
    if (presetDone || !preset || docTypes === null) return;
    setPresetDone(true);
    if (types.some((t) => t.code === preset)) setType(preset);
  }, [preset, presetDone, docTypes, types]);
  const picked = useMemo(() => types.find((t) => t.code === type) ?? null, [types, type]);
  const formFields = picked?.formSchema?.fields ?? [];
  const measureNum = measure.trim() === '' || !Number.isFinite(Number(measure)) ? null : Number(measure);
  const formHasMeasure = formFields.some((f) => f.measure);
  const asksAmount = suggestion?.finalize?.kind === 'AMOUNT' && !formHasMeasure;
  const fieldCount = (t: DocTypeDto) => t.formSchema?.fields.length ?? 0;

  /**
   * 문서를 만든다 — **올리기**와 **임시저장**이 같은 길을 탄다 (2026-09-04 · 판 P2).
   *
   * ★★ 다른 것은 **결재선을 요구하는가** 하나뿐이다. 임시저장은 「아직 아무에게도 안 갔다」는
   *    뜻이라 결재자·최종결재자 칸이 비어 있어도 저장된다 — 서버도 같은 규칙이다.
   *    그 자리에서 막으면 **가장 필요한 순간에 못 쓰는 기능**이 된다(빈 채로 접어 두려는 것이니까).
   */
  const save = useCallback(async (asDraft: boolean) => {
    if (!type) return;
    /**
     * ★★★ **두 번 올라가는 것을 여기서 막는다** (2026-09-05 · 사용자 지적
     *    「지출결의 작성중에 올리기 누르면 계속 올라간다」 — 10초에 문서 8건이 실제로 생겼다).
     *
     * ★★ `submitting` **상태로는 못 막는다.** React 상태는 다음 그림에서야 반영되므로,
     *    그 사이에 들어온 두 번째 호출은 `submitting` 을 아직 `false` 로 본다.
     *    단추의 `disabled` 도 마찬가지다 — 게다가 **Enter 는 단추를 거치지 않는다**.
     * ★ 그래서 **ref** 로 «지금 올리는 중인가» 를 본다(그림과 무관하게 즉시 바뀐다).
     */
    if (sendingRef.current) return;
    sendingRef.current = true;
    setSubmitting(true); setError(null);
    try {
      const approverIds = line.approvers.map((a) => a.userId);
      if (!asDraft && approverIds.length === 0) throw new Error('결재자를 한 명 이상 고르세요.');
      /**
       * ★★★ **필수 「고르기」 칸은 여기서 막는다** (2026-09-07 · 정본 채택 3단계 · 결재 판 1).
       *
       *    서식 칸이 정본 `Select` 로 갈아타면서 네이티브 `<select required>` 가 사라졌다 —
       *    고르개는 `<button>` 이라 브라우저가 폼을 붙잡아 주지 않는다. 안 막으면 빈 채로
       *    올라가 **서버가 400 으로 되돌려 주는 것**이 첫 신호가 된다.
       * ★ **고르기 칸만** 본다. 글자·숫자·날짜는 여전히 네이티브가 막는다 —
       *   여기서 함께 재면 종전에 올라가던 것을 새로 막게 된다(회귀).
       * ★ 임시저장은 안 막는다 — 「아직 아무에게도 안 갔다」는 뜻이라 빈 칸이 있어도 접어 둔다.
       */
      if (!asDraft) {
        const missing = formFields.find(
          (f) => f.kind === 'select' && f.required && selectCode(formValues[f.key]) === '',
        );
        if (missing) throw new Error(`${missing.label} 을(를) 고르세요.`);
      }
      const res = await createDocument({
        title, type, content, contentType, approverIds,
        // ★ 회의록에서 넘어온 기안만 싣는다 — 옛 서버는 모르는 칸이라 400 이다(위 넷과 같은 규약).
        //   그 400 은 `createDocument` 가 받아 이 칸만 빼고 한 번 다시 올린다.
        ...(minutesId ? { minutesId } : {}),
        ...(asDraft ? { saveAsDraft: true } : {}),
        // ★ 아래 넷은 **있을 때만** 싣는다 — 옛 서버는 `additionalProperties:false` 라 400 이다.
        ...(line.referrers.length > 0 ? { referrerIds: line.referrers.map((r) => r.userId) } : {}),
        ...(attachState.attachments.length > 0
          ? { attachments: attachState.attachments.map((a) => ({ fileId: a.fileId })) } : {}),
        ...(measureNum !== null ? { measureValue: measureNum } : {}),
        ...(Object.keys(formValues).length > 0 ? { formData: formValues } : {}),
        // 부서 결재함 — **고른 것이 있을 때만** 싣는다(옛 서버는 모르는 칸이라 400 이다).
        ...(deptBoxId ? { deptBoxId } : {}),
        isUrgent,
      });
      // 올리고 나면 **내가 올린 것**으로 보낸다 — 방금 올린 문서가 거기 있다.
      useApprovalStore.getState().bumpApprovalStatus();
      setSubmitted(true);   // ★ 여기부터는 잃을 것이 없다 — 이탈 경고를 끈다.
      // ★ 임시저장은 근태에 아무것도 안 만든다(서버가 그렇게 한다) — 「임시」로 보낸다.
      if (asDraft) { navigate('/approval/temp'); return; }
      // 근태에 안 들어갔으면 **여기 남아서 말한다**(옛 서버는 이 칸을 안 줘서 그냥 넘어간다).
      if (res.hrRequest && !res.hrRequest.made && res.hrRequest.reason) {
        setHrNotice(res.hrRequest.reason);
        return;
      }
      // 지출 기록을 아예 못 만든 경우도 같다(금액을 숫자로 못 읽은 줄이 있을 때 — 판 B1).
      if (res.expenseRequest && !res.expenseRequest.made && res.expenseRequest.reason) {
        setHrNotice(res.expenseRequest.reason);
        return;
      }
      /*
       * 회의록 이력에 못 적혔으면 **여기 남아서 말한다** — 위 근태·지출과 «같은 규칙»이다.
       * ⚠ `=== false` 만 본다. 옛 서버는 이 칸을 **안 주므로** `undefined` 이고, 그때는
       *   「안 남았다」가 아니라 **「모른다」**다 — 모르는 것을 말하지 않는다.
       *   (그 칸을 실었다가 400 으로 되물려 올린 경우는 `createDocument` 가 `false` 로 알려 준다.)
       */
      /*
       * ★★★ **「아직 적을 때가 아니다」는 «경고가 아니다»** (2026-09-22 · 서버 #1641 · additive).
       *
       * 임시저장은 이력을 **일부러 안 적는다**(그것이 맞다 — 아직 아무에게도 안 갔다). 그때 서버는
       * `minutesLogged:false` 와 함께 **`minutesLogSkipped:'draft'`** 를 준다. 그 칸이 «있으면»
       * 화면은 **아무 말도 하지 않는다** — 말하면 화면이 거짓말을 한다.
       * ★ 칸이 «없는» `false` 는 종전 그대로 **말한다**(진짜로 못 적은 것 · 옛 서버가 그 칸을 몰라
       *   되물려 올린 것). ⇒ 「칸 부재 = 종전 동작」이 이 가드의 계약이다.
       */
      if (res.minutesLogged === false && !res.minutesLogSkipped) {
        /*
         * ★ 머리글이 이미 「남지 않았습니다」를 말한다(아래 렌더) — 여기는 **까닭만** 적는다.
         *   기존 `hrNotice` 와 같은 짝이다(head = 무슨 일 · why = 까닭).
         */
        setMinutesNotice(res.minutesIdRejected === true
          ? '이 서버가 회의록 연결을 아직 몰라 적지 못했습니다. 결재 문서 자체는 정상입니다.'
          : '누구에게 갔는지 회의록 화면에서 되짚을 수 없습니다.');
        return;
      }
      /**
       * ★★★ **예산에 못 붙은 것은 «실패»가 아니다** (판 B3) — 예산을 안 짠 회사도 쓴다.
       *    그래서 여기 붙잡아 두지 않고 **문서함으로 보내면서** 그 사실을 함께 띄운다.
       */
      const notes = res.expenseRequest?.notes ?? [];
      if (notes.length > 0) { setBudgetNotes(notes); return; }
      navigate('/approval/drafts');
    } catch (err) {
      /*
       * ⛔ **기안 종류를 못 쓰는 두 경우는 «갈라» 말한다** (2026-09-22 · #1626).
       *
       *   UNKNOWN_DOC_TYPE   그런 종류가 «없다» — 사용자가 할 수 있는 것이 없다
       *   INACTIVE_DOC_TYPE  회사가 «껐다»    — ★ 관리자가 켜면 «된다»
       *
       * 뭉치면 「없는 종류입니다」로만 보여 사용자는 **관리자에게 물을 생각을 못 한다.**
       * ★ 붉은 한 줄(`error`)이 아니라 **눈에 띄는 알림**으로 띄운다 — 「관리자에게 문의」는
       *   «행동»을 요구하는 말이고, 한 줄짜리 오류에 섞이면 읽히지 않는다.
       *   (이 화면의 근태·예산 알림과 같은 자리·같은 모양이다.)
       */
      const code = extractErrorCode(err);
      if (code === UNKNOWN_DOC_TYPE_CODE || code === INACTIVE_DOC_TYPE_CODE) {
        setDocTypeNotice({ code, message: extractErrorMessage(err) });
        return;
      }
      setError(extractErrorMessage(err));
    } finally { sendingRef.current = false; setSubmitting(false); }
  }, [type, title, content, line, attachState.attachments, measureNum, formValues, formFields, isUrgent, navigate]);

  const submit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    await save(false);
  }, [save]);

  /* ══════════ 첫 걸음 — 양식 고르기 (시안 B) ══════════ */
  if (!type) {
    const recentTypes = recent.map((c) => types.find((t) => t.code === c)).filter(Boolean) as DocTypeDto[];
    const rest = types.filter((t) => !recent.includes(t.code));
    return (
      <div className="apn apn--pick">
        <header className="apn__head">
          <div>
            <h1>무엇을 올리시겠습니까</h1>
            <p className="apn__sub">회사가 연 양식 {types.length}종 — 고르면 그 양식의 칸이 그려집니다.</p>
          </div>
        </header>

        {recentTypes.length > 0 && (
          <>
            <h2 className="apn__grp">최근에 쓴 것</h2>
            <div className="apn__cards">
              {recentTypes.map((t) => (
                <button key={t.id ?? t.code} type="button" className="apn__card" onClick={() => setType(t.code)}>
                  <b>{t.name}</b>
                  <span>{fieldCount(t) > 0 ? `칸 ${fieldCount(t)}` : '제목과 본문만'}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {GROUPS.map((g) => {
          const rows = rest.filter((t) => (t.category ?? 'GENERAL') === g.key);
          if (rows.length === 0) return null;
          return (
            <div key={g.key}>
              <h2 className="apn__grp">{g.label}</h2>
              <div className="apn__cards">
                {rows.map((t) => (
                  <button key={t.id ?? t.code} type="button" className="apn__card" onClick={() => setType(t.code)}>
                    <b>{t.name}</b>
                    <span>{fieldCount(t) > 0 ? `칸 ${fieldCount(t)}` : '제목과 본문만'}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  /* ══════════ 기안 (시안 A · 칸이 없으면 C) ══════════ */
  const steps = [
    { head: '기안', name: me?.name ?? '나', done: true },
    ...line.approvers.map((a, i) => ({ head: `${i + 1}단계`, name: a.name, done: false })),
  ].slice(0, 4);

  return (
    <>
    {/*
      ★★★ 쓰던 것을 두고 나가려 하면 묻는다 (2026-09-08 사용자 지시 · 정본 P13).
         기안은 서식 칸이 마흔까지 가는 자리라 **잃을 것이 가장 크다.**
    */}
    <UnsavedGuard when={dirty} />
    <form
      className="apn"
      onSubmit={submit}
      /**
       * ★★★ **한 줄 입력칸에서 Enter 는 «올리기» 가 아니다** (2026-09-05).
       *
       *    지출결의서는 표에 사용일·금액을 채우며 **Enter 로 칸을 넘어가게 된다.** 그런데
       *    `<form>` 안의 한 줄 입력칸에서 Enter 를 치면 **브라우저가 폼을 올린다** —
       *    단추를 안 눌렀는데 문서가 생긴다. 실제로 **10초에 여덟 건**이 올라갔다.
       * ★★ 여러 줄 칸(`textarea`)과 단추는 그대로 둔다 — 거기서 Enter 는 줄바꿈·누르기다.
       * ★ 올리는 길은 **「올리기」 단추 하나**로 남는다(뜻이 분명해진다).
       */
      onKeyDown={(e) => {
        if (e.key !== 'Enter') return;
        const el = e.target as HTMLElement;
        if (el.tagName === 'INPUT' && (el as HTMLInputElement).type !== 'submit') {
          e.preventDefault();
        }
      }}
    >
      <header className="apn__head">
        <div>
          <h1>기안</h1>
          <p className="apn__sub">{picked?.name ?? type}</p>
        </div>
        <span className="apn__sp" />
        {!previewing && (
          <Button
            variant="secondary" size="compact" onClick={() => setType(null)} disabled={submitting}
            iconLeft={<ArrowLeft size={13} aria-hidden />}
          >
            양식 바꾸기
          </Button>
        )}
        {previewing ? (
          <Button
            variant="secondary" size="compact" onClick={() => window.print()} disabled={submitting}
            iconLeft={<Printer size={13} aria-hidden />}
          >
            인쇄
          </Button>
        ) : (
          <Button variant="secondary" size="compact" onClick={() => navigate('/approval')} disabled={submitting}>
            취소
          </Button>
        )}
        {/* ★ 「올리기」 옆에 둔다 — 올리기 직전이 미리 보고 싶은 순간이다. */}
        <Button
          variant="secondary" size="compact" disabled={submitting}
          onClick={() => setPreviewing((v) => !v)}
          iconLeft={<Eye size={13} aria-hidden />}
        >
          {previewing ? '다시 쓰기' : '미리보기'}
        </Button>
        {/*
          ★★ **임시저장** (판 P2) — 결재선이 비어 있어도 눌린다. 「아직 아무에게도 안 갔다」는
             뜻이라 근태 행도 문서번호도 알림도 생기지 않는다(서버가 그렇게 한다).
        */}
        <Button
          variant="secondary" size="compact" disabled={submitting || hrNotice !== null}
          onClick={() => { void save(true); }}
        >
          임시저장
        </Button>
        <Button type="submit" size="compact" disabled={submitting || hrNotice !== null}>
          {submitting ? '올리는 중…' : '올리기'}
        </Button>
      </header>

      {error && <p className="apn__error" role="alert">{error}</p>}

      {/*
        ★★★ **올라갔지만 근태에는 안 들어갔다** (판 C).
           서식을 회사가 고칠 수 있어서(「기간」 칸을 날짜 하나로 바꾼 회사가 있었다) 재료가
           안 맞으면 서버는 **행을 지어내지 않는다.** 그 사실을 여기서 말한다 — 조용히 넘어가면
           결재는 끝났는데 연차는 그대로인 상태를 아무도 못 본다.
      */}
      {hrNotice && (
        <div className="apn__hrnotice" role="alert">
          <p className="apn__hrnotice-head">문서는 올라갔습니다. 다만 <b>근태에는 반영되지 않았습니다.</b></p>
          <p className="apn__hrnotice-why">{hrNotice}</p>
          <Button variant="secondary" size="compact" className="apn__noticebtn" onClick={() => navigate('/approval/drafts')}>
            내 문서함으로
          </Button>
        </div>
      )}

      {/*
        ★★ **올라갔지만 회의록 이력에는 안 남았다** (AI 회의록 보내기 · 2026-09-22).
           위 근태 알림과 **같은 자리·같은 규칙**이다(여기 남아서 말한다). 뜻은 다르다 —
           회의록은 **되돌리기가 없어** 그 이력이 「누구에게 갔는지」를 되짚을 유일한 길이다.
           ⛔ 조용히 넘어가면 사람은 **안 남은 것을 모른다.**
      */}
      {/*
        ★★ **기안 종류를 못 쓴다** (2026-09-22 · #1626) — 둘을 «갈라» 말한다.
           `INACTIVE_DOC_TYPE` 은 **관리자가 켜면 된다** — 그 한 마디가 있어야 사람이 어디로
           갈지 안다. ⛔ 「없는 종류입니다」로 뭉치면 관리자에게 물을 생각을 못 한다.
      */}
      {docTypeNotice && (
        <div className="apn__hrnotice" role="alert">
          <p className="apn__hrnotice-head">
            {docTypeNotice.code === INACTIVE_DOC_TYPE_CODE
              ? <>이 결재 종류는 <b>지금 쓸 수 없습니다.</b></>
              : <>이 결재 종류는 <b>없는 종류입니다.</b></>}
          </p>
          <p className="apn__hrnotice-why">{docTypeNotice.message}</p>
          <p className="apn__hrnotice-why">
            {docTypeNotice.code === INACTIVE_DOC_TYPE_CODE
              ? '관리자가 다시 켜면 그대로 쓸 수 있습니다. 급하면 다른 종류를 골라 주세요.'
              : '다른 종류를 골라 주세요. 화면이 오래된 것이라면 새로 고치면 목록이 바뀝니다.'}
          </p>
        </div>
      )}

      {minutesNotice && (
        <div className="apn__hrnotice" role="alert">
          <p className="apn__hrnotice-head">결재는 올라갔습니다. 다만 <b>회의록의 「보낸 이력」에는 남지 않았습니다.</b></p>
          <p className="apn__hrnotice-why">{minutesNotice}</p>
          <Button variant="secondary" size="compact" className="apn__noticebtn" onClick={() => navigate('/approval/drafts')}>
            내 문서함으로
          </Button>
        </div>
      )}

      {/*
        ★★★ **예산 없이 올렸다** (2026-09-04 · 판 B3) — 위의 「반영 안 됨」과 **다른 말**이다.
           막지도, 엉뚱한 예산을 지어내지도 않는다. 지출 기록은 남았고 예산만 안 붙었다는 뜻이라
           **어느 줄이 왜 못 붙었는지**를 적고 그대로 넘어간다.
      */}
      {budgetNotes.length > 0 && (
        <div className="apn__hrnotice apn__hrnotice--soft" role="status">
          <p className="apn__hrnotice-head">문서는 올라갔습니다. 다만 <b>예산에는 붙지 않았습니다.</b></p>
          <ul className="apn__budgetnotes">
            {budgetNotes.map((n) => <li key={n}>{n}</li>)}
          </ul>
          <p className="apn__hrnotice-why">
            지출 기록은 남습니다 — 나중에 예산이 생기면 담당자가 이어 붙일 수 있습니다.
          </p>
          <Button variant="secondary" size="compact" className="apn__noticebtn" onClick={() => navigate('/approval/drafts')}>
            내 문서함으로
          </Button>
        </div>
      )}

      {/*
        ★★★ **미리보기는 «같은 값, 다른 옷»** (2026-09-01 · 판 13 ③).
           저장하지 않는다 — 화면 안에서 모습만 바꾼다. 그래서 새 API 가 하나도 없다.
        ★ 오른쪽 기둥(결재선·첨부·옵션)은 접는다 — 종이에는 없는 것들이다.
      */}
      {previewing ? (
        <ApprovalPreview
          typeName={picked?.name ?? type}
          title={title}
          content={content}
          drafterName={me?.name ?? '나'}
          belong={[suggestion?.divisionName, suggestion?.teamName].filter(Boolean).join(' ') || '—'}
          today={today()}
          line={line}
          suggestion={suggestion}
          fields={formFields}
          values={formValues}
          attachments={attachState.attachments}
          isUrgent={isUrgent}
          measure={measureNum}
        />
      ) : (

      <div className="apn__body">
        <div className="apn__doc">
          {/* ── 문서 머리 — 결재란과 기안자 ── */}
          <div className="apn__sheet">
            <div className="apn__sheettop">
              {/* 정본 B15 「문서 제목 칸」 (2026-09-07 신설) — 밑줄 하나로 말하는 칸이다.
                  ★ 밑줄이 `--border-strong`(라이트 1.48:1)에서 `--input-border`(4.76)로 올라간다. */}
              <DocTitleInput
                wrapperClassName="apn__ttl"
                label="제목"
                value={title} maxLength={500} required autoFocus
                placeholder={`${picked?.name ?? ''} 제목`}
                onChange={(e) => setTitle(e.target.value)}
              />
              {/* ★ 결재란은 **문서 안**에 있다 — 인쇄·PDF 로 나갈 때 도장이 함께 나가야 한다. */}
              <div className="apn__appr" aria-label="결재란">
                {steps.map((s, i) => (
                  <div className="apn__apprc" key={`${s.head}-${i}`}>
                    <div className="apn__apprh">{s.head}</div>
                    <div className="apn__apprb">
                      <span className={`apn__stamp${s.done ? '' : ' is-wait'}`}>
                        {s.done ? (s.name ?? '나').slice(0, 2) : '대기'}
                      </span>
                    </div>
                    <div className="apn__apprn">{s.done ? today().slice(5) : '—'}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="apn__meta">
              <div><span>기안자</span><b>{me?.name ?? '—'}</b></div>
              <div><span>소속</span><b>
                {[suggestion?.divisionName, suggestion?.teamName].filter(Boolean).join(' ') || '—'}
              </b></div>
              <div><span>기안일</span><b>{today()}</b></div>
            </div>
          </div>

          {/* ── 서식 칸 — 없으면 아무것도 안 그린다(종전 그대로) ── */}
          <ApprovalFormFields
            fields={formFields}
            value={formValues}
            onChange={setFormValues}
            disabled={submitting}
          />

          {/*
            ★★★ **예산은 «올리기 전에» 말한다** (2026-09-04 · 예산 판 B4).
               판 B3 까지는 올린 뒤에야 「예산에 못 붙었다」를 알았고, 그때는 이미 결재를
               태운 뒤라 고칠 수가 없었다. 물어볼 자리를 앞으로 옮긴다.
            ★★ **막지 않는다**(결정 B5-a) — 넘는다고 말할 뿐이고 올릴지는 사람이 정한다.
            ★ 지출결의서가 아니거나 예산이 없으면 **아무것도 안 그린다**(조용히 접힌다).
          */}
          <BudgetHint fields={formFields} values={formValues} />

          {/* ★ 안내문을 `<label>` **밖**에 둔다 — 안에 두면 그 글자가 본문 칸의 «이름»에 붙어
                읽어 주는 이름이 「본문 이 양식에는 칸이 없습니다…」가 된다(서식 칸이 겪은 것과 같다). */}
          {/*
            ★★ **서식이 있으면 본문은 선택이다** (2026-09-04 · 판 P3 · 결정 ⑩).
               칸이 값을 나르는데 본문까지 필수면, 지출결의서를 다 채워 놓고도 「본문」에
               아무 말이나 적어야 올라간다 — 뜻 없는 글자가 문서마다 쌓인다.
               서버도 같은 판정이다(칸이 없는 양식일 때만 400).
          */}
          <FormField
            className="apn__field"
            label={<>본문{formFields.length > 0 && <small className="apn__opt"> · 선택</small>}</>}
            required={formFields.length === 0}
          >
            <Textarea
              fullWidth value={content} rows={formFields.length > 0 ? 4 : 8}
              placeholder={formFields.length > 0 ? '위 칸이 담지 못하는 말이 있으면 적습니다' : undefined}
              onChange={(e) => setContent(e.target.value)}
            />
          </FormField>
          {formFields.length === 0 && (
            <p className="apn__hint">
              이 양식에는 칸이 없습니다 — 「양식 · 서식」에서 칸을 갖추면 여기 함께 뜹니다.
            </p>
          )}

          {asksAmount && (
            <FormField className="apn__field apn__field--sm" label="금액">
              <Input
                fullWidth type="number" min={0} step={1000} value={measure} placeholder="0"
                onChange={(e) => setMeasure(e.target.value)}
              />
            </FormField>
          )}
        </div>

        {/* ── 오른쪽 기둥 — 종류와 무관한 것 ── */}
        <aside className="apn__side">
          <ApprovalLineBlock
            docType={type}
            measure={measureNum}
            value={line}
            onChange={setLine}
            disabled={submitting}
            onError={setError}
            onSuggestion={setSuggestion}
          />

          <div className="apn__sidesec">
            <h2 className="apn__sidet">첨부</h2>
            <AttachmentUploader state={attachState} disabled={submitting} />
            <Button
              variant="secondary" size="compact" className="apn__dashed" fullWidth
              onClick={() => setWsPickerOpen(true)}
              disabled={submitting || attachState.attachments.length >= MAX_ATTACHMENTS}
              iconLeft={<HardDrive size={13} aria-hidden />}
            >
              워크스페이스에서 고르기
            </Button>
            {/*
              ★★ **AI 첨부파일 — 아직 안 되는 것을 «될 것»으로 세운다** (2026-09-01).

                 영수증을 이미지·PDF 로 받아 **읽어서 지출 내역 표를 채우는** 자리다. 지금은
                 읽는 코드가 없으므로 **눌리지 않게** 두고(`disabled`), 무엇이 될 자리인지만 적는다.
              ★ 「할 수 없는 일을 메뉴에 세우면 그게 잘못된 안내」라는 규칙과 어긋나지 않는다 —
                어긋나는 것은 **되는 척**하는 것이고, 여기는 «추후 예정»이라고 화면이 먼저 말한다.
                누를 수 있게 두면 눌러 보고 아무 일도 안 일어나는 것이 그 거짓말이 된다.
            */}
            <Button
              variant="secondary" size="compact" className="apn__dashed" fullWidth
              disabled aria-describedby="apn-ai-soon"
              iconLeft={<Sparkles size={13} aria-hidden />}
            >
              AI 첨부파일
              <span className="apn__soon">추후 예정</span>
            </Button>
            <p className="apn__hint" id="apn-ai-soon">
              영수증을 <b>사진·PDF</b> 로 올리면 읽어서 <b>지출 내역 표를 자동으로 채웁니다</b> —
              준비 중인 기능입니다.
            </p>

            {/* ★ 이 두 가지는 **짐작할 수 없다** — 화면이 말해 주지 않으면 아무도 모른다. */}
            <p className="apn__hint">
              워크스페이스에 이미 있는 파일은 <b>다시 올리지 않아도</b> 붙습니다 —
              공유받은 자료·회사 자료도 <b>읽을 수 있으면</b> 그대로 첨부됩니다.
            </p>
          </div>

          <div className="apn__sidesec">
            <h2 className="apn__sidet">옵션</h2>
            <Checkbox
              label="긴급 결재" checked={isUrgent}
              onChange={(e) => setIsUrgent(e.target.checked)}
            />
          </div>
        </aside>
      </div>
      )}


      {/* ★ `allowOthers` — 결재 첨부는 소유자가 아니라 **읽을 수 있느냐**로 판정한다(서버도 같다). */}
      <WorkspaceFilePicker
        open={wsPickerOpen}
        myUserId={myUserId}
        allowOthers
        pickedIds={attachState.attachments.map((a) => a.fileId)}
        room={MAX_ATTACHMENTS - attachState.attachments.length}
        onClose={() => setWsPickerOpen(false)}
        onPick={(items) => { attachState.addExisting(items); setWsPickerOpen(false); }}
      />
    </form>
    </>
  );
}

export default ApprovalNewPage;
